import axios from 'axios'
import { Field, Form, Formik } from 'formik'
import { graphql, useStaticQuery } from 'gatsby'
import qs from 'qs'
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import * as Yup from 'yup'

import Link from '../components/link'
import { classes } from '../util/components'
import Dropdown, { DropdownItem } from './dropdown'
import Modal from './modal'
import RadioButton from './radio-button'
import Tab from './tab'
import Tabs from './tabs'
import styles from './venue-hire-request.module.css'

const useContent = () => {
  const result = useStaticQuery(
    graphql`
      query {
        form: allMarkdownRemark(
          filter: { fields: { slug: { eq: "/venue-hire-request/" } } }
        ) {
          edges {
            node {
              frontmatter {
                title
                leadInHtml
                links {
                  title
                  url
                }
                eventForm {
                  formFields {
                    dates {
                      label
                      hint
                      required
                    }
                    time {
                      label
                      hint
                      required
                    }
                    venue {
                      label
                      hint
                      required
                    }
                    package {
                      label
                      hint
                      required
                    }
                    numberOfGuests {
                      label
                      hint
                      required
                    }
                    description {
                      label
                      hint
                      required
                    }
                    catering {
                      label
                      hint
                      required
                    }
                  }
                }
                contactForm {
                  formFields {
                    name {
                      label
                      hint
                      required
                    }
                    email {
                      label
                      hint
                      required
                    }
                    organization {
                      label
                      hint
                      required
                    }
                    phone {
                      label
                      hint
                      required
                    }
                    questionsAndComments {
                      label
                      hint
                      required
                    }
                    contactMethod {
                      label
                      hint
                      required
                    }
                  }
                }
              }
            }
          }
        }
        rentalPackages: allMarkdownRemark(
          filter: { fields: { collection: { eq: "rental-packages" } } }
        ) {
          nodes {
            frontmatter {
              title
            }
          }
        }
        venues: allMarkdownRemark(
          filter: {
            fields: { collection: { eq: "venues" } }
            frontmatter: { availableForHire: { eq: true } }
          }
        ) {
          nodes {
            fields {
              slug
            }
            frontmatter {
              title
              capacity
            }
          }
        }
      }
    `
  )

  if (result.form.edges.length) {
    const {
      node: { frontmatter: form },
    } = result.form.edges[0]

    const rentalPackages = result.rentalPackages.nodes.map(e => e.frontmatter)
    const venues = result.venues.nodes.map(e => ({
      ...e.frontmatter,
      ...e.fields,
    }))

    return { form, rentalPackages, venues }
  }

  return null
}

const getValidationSchema = (formInfo, initialSchema) =>
  Yup.object().shape({
    ...Object.keys(formInfo.formFields).reduce((schema, f) => {
      if (formInfo.formFields[f].required) {
        schema[f] = schema[f].required()
      }

      return schema
    }, initialSchema),
  })

const RequestField = ({ name, label, hint, required, className, children }) => (
  <div className={classes(styles.formGroup, className)}>
    <label htmlFor={name} className={styles.formLabel}>
      {label}
      {required && '*'}
    </label>
    <p className={styles.formHint}>{hint}</p>
    {children}
  </div>
)

const VenueHireRequestEventInfo = ({
  formInfo,
  initialValue,
  schema,
  venues,
  packages,
  onSubmit,
}) => {
  let reset

  useEffect(() => {
    if (reset) {
      reset(initialValue)
    }
  }, [reset, initialValue])

  if (!formInfo) {
    return null
  }

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={initialValue}
    >
      {({ errors, touched, resetForm }) => {
        reset = resetForm
        return (
          <Form className={styles.form}>
            <RequestField
              name="dates"
              {...formInfo.formFields.dates}
              className={classes(
                errors.dates && touched.dates && styles.hasError
              )}
            >
              <Field type="text" name="dates" />
            </RequestField>
            <RequestField
              name="time"
              {...formInfo.formFields.time}
              className={classes(
                errors.time && touched.time && styles.hasError
              )}
            >
              <Field type="text" name="time" />
            </RequestField>
            <RequestField
              name="venue"
              {...formInfo.formFields.venue}
              className={classes(
                errors.venue && touched.venue && styles.hasError
              )}
            >
              <Field name="venue">
                {({ field }) => (
                  <Dropdown
                    className={styles.dropdown}
                    selected={field.value}
                    multiple={true}
                    placeholder="Select venue(s)"
                    error={errors.venue}
                    onClick={val =>
                      field.onChange('venue')(
                        field.value.includes(val)
                          ? field.value.filter(f => f !== val)
                          : [val, ...field.value]
                      )
                    }
                  >
                    {Object.keys(venues).map((k, i) => (
                      <DropdownItem value={k} key={i + 1}>
                        {venues[k]}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </Field>
            </RequestField>
            <RequestField
              name="package"
              {...formInfo.formFields.package}
              className={classes(
                errors.package && touched.package && styles.hasError
              )}
            >
              <Field name="package">
                {({ field }) => (
                  <Dropdown
                    className={styles.dropdown}
                    selected={field.value}
                    placeholder="Select package"
                    error={errors.package}
                    onClick={field.onChange('package')}
                  >
                    {(packages || []).map((p, i) => (
                      <DropdownItem value={p} key={i + 1}>
                        {p}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                )}
              </Field>
            </RequestField>
            <RequestField
              name="numberOfGuests"
              {...formInfo.formFields.numberOfGuests}
              className={classes(
                errors.numberOfGuests &&
                  touched.numberOfGuests &&
                  styles.hasError
              )}
            >
              <Field type="text" name="numberOfGuests" />
            </RequestField>
            <RequestField
              name="catering"
              {...formInfo.formFields.catering}
              className={classes(
                errors.catering && touched.catering && styles.hasError
              )}
            >
              <Field name="catering">
                {({ field }) => (
                  <div className={styles.radioButtons}>
                    <RadioButton
                      className={styles.radioButton}
                      field={field}
                      name={field.name}
                      error={errors.catering}
                      touched={touched.catering}
                      value="yes"
                      text="Yes"
                    />
                    <RadioButton
                      className={styles.radioButton}
                      field={field}
                      name={field.name}
                      error={errors.catering}
                      touched={touched.catering}
                      value="no"
                      text="No"
                    />
                  </div>
                )}
              </Field>
            </RequestField>
            <RequestField
              name="description"
              {...formInfo.formFields.description}
              className={classes(
                styles.wide,
                errors.description && touched.description && styles.hasError
              )}
            >
              <Field component="textarea" name="description" />
            </RequestField>
            <div className={styles.formButtons}>
              <button
                className={classes('btn-primary', styles.btnNext)}
                type="submit"
              >
                Next: Your contact information
              </button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

const VenueHireRequestContactInfo = ({
  formInfo,
  initialValue,
  schema,
  onPrevious,
  onSubmit,
}) => {
  let reset
  const formRef = createRef()

  useEffect(() => {
    if (reset) {
      reset(initialValue)
    }
  }, [reset, initialValue])

  return (
    <Formik
      validationSchema={schema}
      onSubmit={val => onSubmit(val)}
      initialValues={initialValue}
    >
      {({ errors, touched, resetForm, handleReset, handleSubmit }) => {
        reset = resetForm

        return (
          <form
            name="venue-hire"
            onReset={handleReset}
            onSubmit={handleSubmit}
            ref={formRef}
            className={styles.form}
            data-netlify="true"
            netlify-honeypot="honeypot"
            method="POST"
          >
            <span className={styles.honeypot}>
              <Field type="text" name="honeypot" />
            </span>
            <Field type="hidden" name="subject" />
            <Field type="hidden" name="dates" />
            <Field type="hidden" name="time" />
            <Field type="hidden" name="venue" />
            <Field type="hidden" name="package" />
            <Field type="hidden" name="numberOfGuests" />
            <Field type="hidden" name="description" />
            <Field type="hidden" name="catering" />
            <RequestField
              name="name"
              {...formInfo.formFields.name}
              className={classes(
                errors.name && touched.name && styles.hasError
              )}
            >
              <Field type="text" name="name" />
            </RequestField>
            <RequestField
              name="email"
              {...formInfo.formFields.email}
              className={classes(
                errors.email && touched.email && styles.hasError
              )}
            >
              <Field type="text" name="email" />
            </RequestField>
            <RequestField
              name="organization"
              {...formInfo.formFields.organization}
              className={classes(
                errors.organization && touched.organization && styles.hasError
              )}
            >
              <Field type="text" name="organization" />
            </RequestField>
            <RequestField
              name="phone"
              {...formInfo.formFields.phone}
              className={classes(
                errors.phone && touched.phone && styles.hasError
              )}
            >
              <Field type="text" name="phone" />
            </RequestField>
            <RequestField
              name="questionsAndComments"
              {...formInfo.formFields.questionsAndComments}
              className={classes(
                styles.wide,
                errors.questionsAndComments &&
                  touched.questionsAndComments &&
                  styles.hasError
              )}
            >
              <Field component="textarea" name="questionsAndComments" />
            </RequestField>
            <RequestField
              name="contactMethod"
              {...formInfo.formFields.contactMethod}
              className={classes(
                errors.contactMethod && touched.contactMethod && styles.hasError
              )}
            >
              <Field name="contactMethod">
                {({ field }) => (
                  <div className={styles.radioButtons}>
                    <RadioButton
                      className={styles.radioButton}
                      field={field}
                      name={field.name}
                      error={errors.contactMethod}
                      touched={touched.contactMethod}
                      value="phone"
                      text="Phone"
                    />
                    <RadioButton
                      className={styles.radioButton}
                      field={field}
                      name={field.name}
                      error={errors.contactMethod}
                      touched={touched.contactMethod}
                      value="email"
                      text="Email"
                    />
                  </div>
                )}
              </Field>
            </RequestField>
            <div className={styles.formButtons}>
              <button
                type="button"
                className="btn-primary"
                onClick={onPrevious}
              >
                Edit event information
              </button>
              <button type="submit" className="btn-primary">
                Submit form
              </button>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

const VenueHireRequest = ({ open, onClose }) => {
  const { form, rentalPackages, venues } = useContent()
  const [tab, setTab] = useState('event')

  const packages = useMemo(() => rentalPackages.map(p => p.title), [
    rentalPackages,
  ])

  const venuesToShow = useMemo(
    () =>
      venues.reduce((g, v) => {
        g[v.title] = `${v.title} ${
          v.capacity ? '(' + v.capacity + ' pax)' : ''
        }`

        return g
      }, {}),
    [venues]
  )

  const selectedVenue = venues.find(
    v =>
      typeof window !== 'undefined' && window.location.pathname.includes(v.slug)
  )

  const initialEventFormData = {
    dates: '',
    time: '',
    venue: selectedVenue ? [selectedVenue.title] : [],
    package: '',
    numberOfGuests: '',
    description: '',
    catering: '',
  }

  const initialContactFormData = {
    ...initialEventFormData,
    subject: '',
    name: '',
    email: '',
    phone: '',
    organization: '',
    questionsAndComments: '',
    contactMethod: '',
    honeypot: '',
  }

  const [completed, setCompleted] = useState(false)
  const [eventFormData, setEventFormData] = useState(initialEventFormData)
  const [contactFormData, setContactFormData] = useState(initialContactFormData)

  const close = useCallback(() => {
    setCompleted(false)
    setTab('event')
    onClose()
  }, [onClose])

  const eventSchema = getValidationSchema(form.eventForm, {
    dates: Yup.string(),
    time: Yup.string(),
    venue: Yup.array().of(Yup.string().oneOf(Object.keys(venuesToShow))),
    package: Yup.string().oneOf(packages),
    numberOfGuests: Yup.string(),
    description: Yup.string(),
    catering: Yup.string().oneOf(['yes', 'no']),
  })

  const contactSchema = eventSchema.concat(
    getValidationSchema(form.contactForm, {
      name: Yup.string(),
      email: Yup.string().email(),
      organization: Yup.string(),
      phone: Yup.string(),
      questionsAndComments: Yup.string(),
      contactMethod: Yup.string().oneOf(['email', 'phone']),
      honeypot: Yup.string().nullable(),
    })
  )

  const submit = val => {
    const subject = `Venue hire enquiry from ${val.name} (${val.organization})`
    typeof window !== "undefined" && window.gtag && window.gtag("event", "submit-venue-enquiry", { 
      event_category: 'Venue Rental Enquiry',
      event_label: 'Projector',
     })
    return axios
      .post(
        '/',
        qs.stringify({
          'form-name': 'venue-hire',
          ...val,
          subject,
        })
      )
      .then(res => {
        if (res.status < 300) {
          setEventFormData(initialEventFormData)
          setContactFormData(initialContactFormData)
          setCompleted(true)
        }
      })
  }

  return (
    <Modal open={open} handleClose={close} className={styles.modal}>
      <div className={styles.wrapper}>
        <h1 className={styles.title}>{form.title}</h1>
        <div
          className={styles.leadIn}
          dangerouslySetInnerHTML={{ __html: form.leadInHtml }}
        ></div>
        {(form.links || []).length ? (
          <div className={styles.links}>
            {form.links.map((link, i) => (
              <Link className="btn-primary" to={link.url} key={i}>
                {link.title}
              </Link>
            ))}
          </div>
        ) : null}
        {completed ? (
          <div className={styles.completed}>
            <p>
              Thanks! We’ll be in touch soon. Meanwhile if you need more details
              on our venues and menus, click on either options below.
            </p>
            <button type="button" className="btn-primary" onClick={close}>
              Close
            </button>
          </div>
        ) : (
          <Tabs className={styles.tabs}>
            <Tab
              title="Event Information"
              isActive={tab === 'event'}
              headerClass={styles.tab}
              omitInactive={false}
            >
              <VenueHireRequestEventInfo
                formInfo={form.eventForm}
                initialValue={eventFormData}
                schema={eventSchema}
                venues={venuesToShow}
                packages={packages}
                onSubmit={val => {
                  setEventFormData(val)
                  setContactFormData({ ...contactFormData, ...val })
                  setTab('contact')
                }}
              />
            </Tab>
            <Tab
              title="Contact Information"
              isActive={tab === 'contact'}
              headerClass={styles.tab}
              omitInactive={false}
            >
              <VenueHireRequestContactInfo
                formInfo={form.contactForm}
                initialValue={contactFormData}
                schema={contactSchema}
                onSubmit={val => {
                  setContactFormData(val)
                  submit(val)
                }}
                onPrevious={() => setTab('event')}
              />
            </Tab>
          </Tabs>
        )}
      </div>
    </Modal>
  )
}

export default VenueHireRequest
